import {Controller} from "@hotwired/stimulus"
import debounce from "lodash/debounce"

/**
 * Left panel controller
 * - allows the sidebar on the left to toggle it's class depending on viewport.
 * - respects user setting stored in local storage
 *
 * usage:
 * %div{data: {controller: 'left-panel'}}  #controller element
 *
 *  %button{data: {action: 'click->left-panel#userClose'}}
 *
 *  %button{data: {action: 'click->left-panel#userOpen'}}
 */

export default class extends Controller {

    storageUserKey = "sidebar-state"
    storagePreviousKey = "sidebar-state-previous"
    collapsedClass = "collapsed"
    breakpointValue = 1200

    connect(){
        this.resizeListener = this._resizeListener.bind(this)
        window.addEventListener('resize', debounce(this.resizeListener, 200))

        this.initFromLocalStorage()
    }

    disconnect() {
        super.disconnect()
        window.removeEventListener('resize', this.resizeListener)
    }

    initFromLocalStorage() {
        const storedClass = localStorage.getItem(this.storageUserKey)
        if(storedClass) {
            this.restoreFromStorage()
        }
        this.responsiveChange()

    }

    _resizeListener(){
        this.responsiveChange()
    }

    restoreFromStorage(){
        const state = localStorage.getItem(this.storageUserKey)
        if(state === "open"){
            this.userOpen()
        } else {
            this.userClose()
        }
    }

    responsiveChange() {
        if(this.breakpointValue < window.innerWidth && this.windowEnlarged()){
            this.responsiveOpen()
        } else if(this.windowShrinked()) {
            this.responsiveClose()
        }
        this.recordSize()
    }

    // always hide the panel for smaller viewport
    responsiveClose() {
        this.element.classList.add(this.collapsedClass)
        this.report("closed")
    }

    // show the panel if user set it to open or user setting does not exist
    responsiveOpen(){
        const storedClass = localStorage.getItem(this.storageUserKey)
        if(!storedClass || storedClass === "open") {
            this.element.classList.remove(this.collapsedClass)
            this.report("open")
        }
    }

    userClose() {
        this.element.classList.add(this.collapsedClass)
        localStorage.setItem(this.storageUserKey, "closed")
        this.report("closed")
    }

    userOpen() {
        this.element.classList.remove(this.collapsedClass)
        localStorage.setItem(this.storageUserKey, "open")
        this.report("open")
    }

    report(state){
        const changeEvent = new CustomEvent('left-panel-change', {detail: {state: state}, bubbles: true})
        this.element.dispatchEvent(changeEvent)
    }

    recordSize() {
        localStorage.setItem(this.storagePreviousKey, window.innerWidth + "")
    }

    // shrink and enlarge detection to prevent panel state changes on
    // viewport changes without crossing the breakpoint
    windowShrinked()
    {
        const previousSize = localStorage.getItem(this.storagePreviousKey)
        return (Number(previousSize) > window.innerWidth)
    }

    windowEnlarged(){
        const previousSize = localStorage.getItem(this.storagePreviousKey)
        return (Number(previousSize) < window.innerWidth)
    }
}